/* =============================================================================
   VIEWS / ADMIN / DAY-CLOSE-ARCHIVE-DETAIL — Z-Bon-Archiv Drill-Down + Re-Actions
   =============================================================================
   v0.76.0-beta / Session 70 / REF-5b Cut 4/4
   ----------------------------------------------------------------------------
   Drill-Down aus dem Z-Bon-Archiv (Auswertung → Archiv → Z-Bons): Detail-
   Modal mit archiviertem Snapshot + Status-Badges + Re-Send/Re-Print
   (Symmetrie zum Bon-Archiv-Pattern).

   Cross-Module:
     - loadZBonArchive aus day-close-zbon-action.js
     - openZBonReceiptActionModal aus day-close-zbon-action.js (via window.*)
     - sendZBonReceiptEmail + printReceiptHtml direkt
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';
import { printReceiptHtml } from './receipt.js';
import { sendZBonReceiptEmail } from '../../core/email-templates.js';
import { getBrandLogoSrc } from '../../core/branding.js';
import { getState, fmtDateDE, centsToEur } from './day-close-state.js';
import { loadZBonArchive } from './day-close-zbon-action.js';

/* -------------------------------------------------------------------------- */
/* S238-HF6 — Snapshot-Isolation (Style-Tag-Global-Leak)                       */
/* -------------------------------------------------------------------------- */
/*
 * BEFUND (Jonas-Eyeball, gemessen): bei offenem Z-Bon-Detail-/Vorschau-Modal war
 * `getComputedStyle(document.body).backgroundColor` = rgb(244,241,234) — das
 * Cockpit hinter dem Modal kippte auf hell und wurde beim Schließen wieder dunkel.
 *
 * URSACHE: beide Modale haben das Standalone-Receipt-CSS aus dem Snapshot per
 * `<style>${css}</style>` via innerHTML in den HAUPT-DOM injiziert. Darin steht
 * `body { background:#f4f1ea … }` — ein `<style>`-Tag ist immer dokument-global,
 * egal wie tief es im Markup hängt. Identisches Muster wie der S230-Weiß-Blitz
 * (receipt-pdf.js), dort schon per iframe gelöst.
 *
 * FIX: den Snapshot als EIGENES Dokument in einem iframe rendern
 * (`srcdoc` = das vollständige archivierte `entry.html`). Style-Isolation by
 * design, kein CSS-Rewrite, Snapshot-Bytes bleiben unangetastet (GoBD).
 *
 * ⚠️ Abweichung vom Handover-Vorschlag `sandbox=""`: ein sandboxed iframe OHNE
 * `allow-same-origin` bekommt eine opake Origin — dann liefert
 * `frame.contentDocument` aus dem Eltern-Dokument `null` und die Höhen-Messung
 * schlägt still fehl (Snapshot bliebe auf der 150-px-Default-Höhe abgeschnitten).
 * Deshalb `sandbox="allow-same-origin"` OHNE `allow-scripts`: Skripte bleiben
 * komplett blockiert (die gefährliche Kombination wäre allow-scripts +
 * allow-same-origin), die Höhe ist messbar, und die Style-Isolation hängt
 * ohnehin am Dokument, nicht an der Origin.
 */

/**
 * S240-HF7 — archivierten Z-Bon-Snapshot lazy holen.
 *
 * Der Listen-Hydrate lädt `html` nicht mehr mit (siehe
 * `ZBON_ARCHIVE_LIST_COLUMNS` in storage.js): im Cache steht `html: null` =
 * „noch nicht geladen". Wer den Snapshot anzeigt, druckt oder versendet, holt
 * ihn hier — sonst stünde überall „Z-Bon-Snapshot nicht gefunden".
 * Fällt auf den Cache zurück, falls der Adapter `getHtml` (noch) nicht kennt.
 */
async function _archivedZBonHtml(dateIso, entry) {
  const za = window.App && window.App.storage && window.App.storage.zbonArchive;
  if (za && typeof za.getHtml === 'function') {
    try { return (await za.getHtml(dateIso)) || ''; }
    catch (e) { console.error('[day-close-archive-detail] getHtml:', e); return ''; }
  }
  return (entry && entry.html) || '';
}

/** Maximale sichtbare Höhe des Snapshot-Frames; darüber scrollt der Frame. */
const SNAPSHOT_MAX_PX = 480;
/** Start-Höhe, bis die Messung greift (verhindert 150-px-Default-Stummel). */
const SNAPSHOT_INIT_PX = 320;

/**
 * Vollständiges Snapshot-Dokument für den iframe. Nutzt das archivierte
 * `entry.html` unverändert — außer dem Logo-Fallback für vor-S29-Snapshots
 * (die wurden ohne Logo-Tag erzeugt; die ANZEIGE ergänzt es, die gespeicherten
 * Bytes bleiben unberührt). Pure — Test-Hook.
 */
export function _buildZBonSnapshotDoc(entry, htmlOverride) {
  // S240-HF7: `htmlOverride` ist der lazy nachgeladene Snapshot; `entry.html`
  // bleibt als Fallback (Realtime-INSERT bringt ihn mit).
  let html = htmlOverride || (entry && entry.html) || '';
  if (!html) return '';
  if (!/<img[^>]*class="receipt-logo"/i.test(html)) {
    const logoTag = `<img class="receipt-logo" src="${htmlEscape(getBrandLogoSrc())}" alt="Logo"`
      + ` style="display:block;max-width:80px;max-height:60px;width:auto;height:auto;margin:0 auto 8px auto;object-fit:contain;">`;
    if (/<(?:header|div)[^>]*class="receipt-header"[^>]*>/i.test(html)) {
      html = html.replace(/(<(?:header|div)[^>]*class="receipt-header"[^>]*>)/i, `$1${logoTag}`);
    } else if (/<body[^>]*>/i.test(html)) {
      html = html.replace(/(<body[^>]*>)/i, `$1${logoTag}`);
    } else {
      html = logoTag + html;
    }
  }
  return html;
}

/**
 * iframe-Markup für den Snapshot. `srcdoc` wird voll HTML-escaped (Attribut-
 * Werte werden vom Parser wieder dekodiert). Pure — Test-Hook.
 */
export function _buildZBonSnapshotFrame(docHtml, opts = {}) {
  if (!docHtml) return '';
  const bg = opts.background || '#ffffff';
  return `<iframe class="zbon-snapshot-frame" sandbox="allow-same-origin" title="Archivierter Z-Bon-Snapshot"`
    + ` style="display:block;width:100%;height:${SNAPSHOT_INIT_PX}px;max-height:${SNAPSHOT_MAX_PX}px;border:0;border-radius:8px;background:${bg};"`
    + ` srcdoc="${htmlEscape(docHtml)}"></iframe>`;
}

/**
 * Höhe des Snapshot-Frames an seinen Inhalt anpassen (max. SNAPSHOT_MAX_PX,
 * darüber scrollt der Frame selbst). Wird nach dem Einhängen aufgerufen —
 * KEIN Inline-`onload`, das der CSP-/dom-bindings-Parser ohnehin nicht könnte.
 */
function _fitSnapshotFrame(root) {
  const frame = root && root.querySelector && root.querySelector('iframe.zbon-snapshot-frame');
  if (!frame) return;
  const fit = () => {
    try {
      const doc = frame.contentDocument;
      const h = doc && doc.body && doc.body.scrollHeight;
      if (!h) return;
      frame.style.height = Math.min(SNAPSHOT_MAX_PX, Math.max(120, h + 24)) + 'px';
    } catch (_) { /* opake Origin / jsdom → Start-Höhe bleibt stehen */ }
  };
  try { frame.addEventListener('load', fit); } catch (_) { /* ignore */ }
  fit();   // srcdoc kann beim Binden schon geparst sein
}

/**
 * Drill-Down aus dem Z-Bon-Archiv: öffnet ein Detail-Modal mit dem
 * archivierten Z-Bon-Snapshot + Meta-/Status-Daten. Analog zu
 * `openOrderDetail` für Bons-/Bestellungen-Archiv.
 *
 * Konsistenz-Pattern (S28 Nachschlag): Alle Archive (Reservierungen,
 * Bestellungen, Bons, Z-Bons) öffnen ein natives Detail-Modal — kein
 * Tab-Wechsel (würde `feedback_gg_no_view_hijack` verletzen) und kein
 * Aktion-Modal (das gehört hinter den „Z-Bon ausgeben"-Button im
 * Tagesabschluss-Tab).
 *
 * Der angezeigte Receipt ist der ARCHIVIERTE Snapshot (`entry.html`),
 * nicht eine erneute DB-Lesung — GoBD-konform (archivierte Belege sind
 * unveränderlich). S238-HF6: der Snapshot rendert in einem eigenen Dokument
 * (iframe/srcdoc) — vorher wurden Body + `<style>` in den Haupt-DOM gehoben,
 * wodurch das Receipt-`body{background:#f4f1ea}` global wirkte und das Cockpit
 * hinter dem Modal auf hell kippte.
 */
export async function viewArchivedZBon(key) {
  const archive = loadZBonArchive();
  // S29: Standard-Lookup über den ISO-Datums-Key (Match mit DB-UNIQUE).
  let entry = archive[key];
  // Backward-Compat: falls der Key noch eine Z-Bon-Nummer ist (alte Renderer-
  // Aufrufe vor S29-Migration), nach entry.zBonNumber durchsuchen.
  if (!entry || !entry.date) {
    for (const k of Object.keys(archive)) {
      const e = archive[k];
      if (e && (e.zBonNumber === key || e.date === key)) {
        entry = e;
        break;
      }
    }
  }
  if (!entry || !entry.date) {
    showToast('Z-Bon-Eintrag nicht gefunden.', 'error');
    return;
  }

  // S238-HF6: Snapshot als eigenes Dokument im iframe statt Body+Style-Extraktion
  // in den Haupt-DOM (das injizierte `body{background:#f4f1ea}` hat das Cockpit
  // hinter dem Modal auf hell gekippt). Der Logo-Fallback für vor-S29-Snapshots
  // steckt jetzt in `_buildZBonSnapshotDoc` — Snapshot-Bytes bleiben unverändert.
  const snapshotDoc = _buildZBonSnapshotDoc(entry, await _archivedZBonHtml(entry.date, entry));

  const zNr = htmlEscape(entry.zBonNumber || '—');
  const dateLabel = htmlEscape(fmtDateDE(entry.date));
  const fmtMoney = (cents) => centsToEur(Number(cents) || 0);

  // Status-Badges analog zur Archiv-Tabelle
  const badges = [];
  if (entry.printedAt)  badges.push('<span class="badge badge-completed">🖨️ gedruckt</span>');
  if (entry.sentAt)     badges.push(`<span class="badge badge-confirmed">✉ versendet${entry.sentTo ? ' an ' + htmlEscape(entry.sentTo) : ''}</span>`);
  if (entry.archivedAt) badges.push('<span class="badge badge-pending">🗄️ archiviert</span>');
  const badgesHtml = badges.length ? badges.join(' ') : '<span style="opacity:.6;">noch keine Aktion</span>';

  // Bezahlmethoden-Split (cash/card/stripe/unpaid)
  const byMethod = entry.byMethod || { cash: 0, card: 0, stripe: 0, unpaid: 0 };
  const methodRows = Object.entries(byMethod)
    .filter(([_, cents]) => Number(cents) > 0)
    .map(([m, cents]) => `<tr><td>${htmlEscape(m)}</td><td class="ta-r">${fmtMoney(cents)}</td></tr>`)
    .join('');

  const invoiceRange = (entry.invoiceFirst || entry.invoiceLast)
    ? `${htmlEscape(entry.invoiceFirst || '—')} bis ${htmlEscape(entry.invoiceLast || '—')}`
    : '—';

  const fmtTs = (iso) => iso ? new Date(iso).toLocaleString('de-DE') : '—';

  const modal = document.createElement('div');
  modal.id = 'zbon-archive-detail-modal';
  modal.className = 'order-modal-overlay';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.innerHTML = `
    <div class="order-modal" onclick="event.stopPropagation()" style="max-width: 720px;">
      <header class="order-modal-header">
        <div>
          <div class="order-modal-eyebrow" style="color:var(--green);">Archivierter Z-Bon</div>
          <h2 class="order-modal-title">Z-Bon ${zNr}</h2>
          <div class="hint" style="margin-top:4px;">${dateLabel} · ${entry.count || 0} Bestellungen · <strong>${fmtMoney(entry.grossCents)}</strong></div>
          <div style="margin-top:8px;">${badgesHtml}</div>
        </div>
        <button class="order-modal-close" onclick="closeViewArchivedZBon()" aria-label="Schließen">×</button>
      </header>

      <!-- S29 Symmetrie-Update: Action-Bar analog zum Bon-Archiv-Detail-Modal
           (openOrderDetail in orders.js). Drei Aktionen in gleicher Reihenfolge:
           Vorschau · Beleg-Aktion · Versand. -->
      <div class="order-modal-actions">
        <button class="btn btn-ghost btn-sm" onclick="previewArchivedZBon('${htmlEscape(entry.date)}')"
                title="Den archivierten Z-Bon mit allen Zeitstempeln und Status-Snapshots anzeigen">
          👁 Z-Bon ansehen
        </button>
        <button class="btn btn-primary btn-sm" onclick="closeViewArchivedZBon(); openZBonReceiptActionModal('${htmlEscape(entry.date)}')"
                title="Beleg-Aktion-Modal: Versenden, Drucken oder Re-Archivieren">
          📋 Beleg-Aktion
        </button>
        <button class="btn btn-ghost btn-sm" onclick="resendArchivedZBonEmail('${htmlEscape(entry.date)}')"
                title="Z-Bon erneut an Steuerberater versenden">
          ✉ Beleg aus Archiv versenden
        </button>
      </div>

      <div class="order-modal-body">

        <section style="margin-bottom: 18px;">
          <h3 style="margin: 0 0 8px; font-size: 0.9rem; text-transform: uppercase; letter-spacing: 0.06em; opacity: 0.7;">Eckdaten</h3>
          <table class="order-modal-items" style="margin: 0;">
            <tbody>
              <tr><td>Brutto</td><td class="ta-r"><strong>${fmtMoney(entry.grossCents)}</strong></td></tr>
              <tr><td>Netto</td><td class="ta-r">${fmtMoney(entry.netCents)}</td></tr>
              <tr><td>MwSt 7 %</td><td class="ta-r">${fmtMoney(entry.tax7Cents)}</td></tr>
              <tr><td>MwSt 19 %</td><td class="ta-r">${fmtMoney(entry.tax19Cents)}</td></tr>
              <tr><td>Bestellungen</td><td class="ta-r">${entry.count || 0}</td></tr>
              ${(entry.countCorrections || 0) > 0 ? `<tr><td>davon Korrekturbelege</td><td class="ta-r">${entry.countCorrections}</td></tr>` : ''}
              ${(entry.countCancelled || 0) > 0 ? `<tr><td>davon storniert</td><td class="ta-r">${entry.countCancelled}</td></tr>` : ''}
              ${(entry.countNoShow || 0) > 0 ? `<tr><td>davon no-show</td><td class="ta-r">${entry.countNoShow}</td></tr>` : ''}
              <tr><td>Rechnungs-Nr-Bereich</td><td class="ta-r">${invoiceRange}</td></tr>
            </tbody>
          </table>
        </section>

        ${methodRows ? `
          <section style="margin-bottom: 18px;">
            <h3 style="margin: 0 0 8px; font-size: 0.9rem; text-transform: uppercase; letter-spacing: 0.06em; opacity: 0.7;">Bezahlmethoden</h3>
            <table class="order-modal-items" style="margin: 0;">
              <tbody>${methodRows}</tbody>
            </table>
          </section>` : ''}

        <section style="margin-bottom: 18px;">
          <h3 style="margin: 0 0 8px; font-size: 0.9rem; text-transform: uppercase; letter-spacing: 0.06em; opacity: 0.7;">Status-Zeitstempel</h3>
          <table class="order-modal-items" style="margin: 0;">
            <tbody>
              <tr><td>Archiviert</td><td class="ta-r">${fmtTs(entry.archivedAt)}</td></tr>
              <tr><td>Gedruckt</td><td class="ta-r">${fmtTs(entry.printedAt)}</td></tr>
              <tr><td>Versendet</td><td class="ta-r">${fmtTs(entry.sentAt)}${entry.sentTo ? '<br><span style="opacity:.7;font-size:.85em;">an ' + htmlEscape(entry.sentTo) + '</span>' : ''}</td></tr>
            </tbody>
          </table>
        </section>

        ${snapshotDoc ? `
          <section style="margin-bottom: 12px;">
            <h3 style="margin: 0 0 8px; font-size: 0.9rem; text-transform: uppercase; letter-spacing: 0.06em; opacity: 0.7;">Archivierter Receipt-Snapshot</h3>
            <div class="zbon-archive-receipt" style="background:var(--bg-elev); border:1px solid var(--border); border-radius: 10px; padding: 10px;">
              ${_buildZBonSnapshotFrame(snapshotDoc)}
            </div>
          </section>` : ''}

      </div>

      <footer class="order-modal-foot" style="padding: 14px 20px; border-top:1px solid var(--border); display:flex; justify-content:flex-end;">
        <button class="btn btn-ghost btn-sm" onclick="closeViewArchivedZBon()">Schließen</button>
      </footer>
    </div>
  `;
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeViewArchivedZBon();
  });
  const onKey = (e) => {
    if (e.key === 'Escape') { closeViewArchivedZBon(); document.removeEventListener('keydown', onKey); }
  };
  document.addEventListener('keydown', onKey);
  document.body.appendChild(modal);
  _fitSnapshotFrame(modal);   // S238-HF6 — Höhe an den Snapshot anpassen
}

export function closeViewArchivedZBon() {
  const m = document.getElementById('zbon-archive-detail-modal');
  if (m) m.remove();
}

/**
 * Vorschau-Modal mit dem reinen Z-Bon-Receipt-HTML — analog
 * `previewOrderReceipt` für Bons. Zeigt den Snapshot in einem eigenen
 * Modal über dem Detail-Modal an, damit man den Bon ohne die ganzen
 * Eckdaten-Tabellen drumherum sehen kann.
 */
export async function previewArchivedZBon(dateIso) {
  const archive = loadZBonArchive();
  const entry = archive[dateIso];
  if (!entry) {
    showToast('Z-Bon-Snapshot nicht gefunden.', 'error');
    return;
  }
  const snapshotHtml = await _archivedZBonHtml(dateIso, entry);
  if (!snapshotHtml) {
    showToast('Z-Bon-Snapshot nicht gefunden.', 'error');
    return;
  }

  // S238-HF6: gleiche Isolation wie im Detail-Modal — der Snapshot rendert in
  // einem eigenen Dokument (iframe/srcdoc), nicht als Body+`<style>` im
  // Haupt-DOM. Logo-Fallback für vor-S29-Snapshots steckt im Doc-Builder.
  const snapshotDoc = _buildZBonSnapshotDoc(entry, snapshotHtml);

  const existing = document.getElementById('zbon-preview-modal');
  if (existing) existing.remove();

  const modal = document.createElement('div');
  modal.id = 'zbon-preview-modal';
  modal.className = 'order-modal-overlay';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.innerHTML = `
    <div class="order-modal" onclick="event.stopPropagation()" style="max-width:640px;">
      <header class="order-modal-header">
        <div>
          <div class="order-modal-eyebrow">Z-Bon-Vorschau</div>
          <h2 class="order-modal-title">Z-Bon ${htmlEscape(entry.zBonNumber || '—')}</h2>
        </div>
        <button class="order-modal-close" onclick="closePreviewArchivedZBon()" aria-label="Schließen">×</button>
      </header>
      <div class="order-modal-body" style="max-height: 70vh; overflow-y: auto; padding: 16px 20px; background: var(--bg-soft);">
        ${_buildZBonSnapshotFrame(snapshotDoc)}
      </div>
      <div class="order-modal-actions" style="border-top:1px solid var(--border, #e3ddd0); padding: 12px 20px;">
        <button class="btn btn-primary btn-sm" onclick="closePreviewArchivedZBon(); reprintArchivedZBon('${htmlEscape(entry.date)}');">
          🖨️ Direkt drucken
        </button>
        <button class="btn btn-ghost btn-sm" style="margin-left:auto;"
                onclick="closePreviewArchivedZBon()">Schließen</button>
      </div>
    </div>
  `;
  modal.addEventListener('click', (e) => { if (e.target === modal) closePreviewArchivedZBon(); });
  const onKey = (e) => {
    if (e.key === 'Escape') { closePreviewArchivedZBon(); document.removeEventListener('keydown', onKey); }
  };
  document.addEventListener('keydown', onKey);
  document.body.appendChild(modal);
  _fitSnapshotFrame(modal);   // S238-HF6 — Höhe an den Snapshot anpassen
}

export function closePreviewArchivedZBon() {
  const m = document.getElementById('zbon-preview-modal');
  if (m) m.remove();
}

/**
 * Archivierten Z-Bon erneut an den Steuerberater versenden.
 *
 * S29: Out-of-the-box-Action im Archiv-Detail-Modal. Nutzt den
 * gespeicherten HTML-Snapshot + denselben sendZBonReceiptEmail-Pfad wie
 * der initiale Versand. Aktualisiert `sentAt`/`sentTo` in DB-Spalten der
 * `zbon_archive`-Tabelle (via upsert) + lokales Re-Render des Archiv-Tabs.
 */
export async function resendArchivedZBonEmail(dateIso) {
  const archive = loadZBonArchive();
  const entry = archive[dateIso];
  if (!entry) {
    showToast('Z-Bon-Snapshot nicht gefunden.', 'error');
    return;
  }
  const snapshotHtml = await _archivedZBonHtml(dateIso, entry);
  if (!snapshotHtml) {
    showToast('Z-Bon-Snapshot nicht gefunden.', 'error');
    return;
  }
  const payload = {
    zBonNumber: entry.zBonNumber,
    dateIso:    entry.date,
    dateLabel:  fmtDateDE(entry.date),
    grossLabel: centsToEur(entry.grossCents),
    count:      entry.count,
    html:       snapshotHtml
  };
  try {
    const result = await sendZBonReceiptEmail(payload);
    if (result && result.success) {
      // sentAt/sentTo aktualisieren — Upsert über die DB
      entry.sentAt = new Date().toISOString();
      entry.sentTo = result.to || (getState().settings || {}).accountantEmail
                                || (getState().settings || {}).restaurantEmail || null;
      if (window.App && window.App.storage && window.App.storage.zbonArchive) {
        await window.App.storage.zbonArchive.upsert(entry);
      }
      showToast(`Z-Bon ${entry.zBonNumber || ''} versendet.`, 'success');
      closeViewArchivedZBon();
      // Archiv-Tab neu rendern, damit der Status-Zeitstempel sichtbar wird.
      if (typeof window.rerenderActiveTab === 'function') window.rerenderActiveTab();
    } else if (result && result.skipped) {
      showToast('Versand übersprungen: ' + (result.reason || 'keine Empfänger-Mail'), 'info');
    } else {
      showToast('Versand fehlgeschlagen: ' + ((result && result.error) || 'unbekannt'), 'error');
    }
  } catch (e) {
    console.error('[resendArchivedZBonEmail]', e);
    showToast('Versand fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

/**
 * Archivierten Z-Bon erneut drucken (Browser-Druckdialog).
 *
 * S29: nutzt den gespeicherten HTML-Snapshot + denselben printReceiptHtml-
 * Pfad wie der initiale Druck. Aktualisiert `printedAt` in der DB.
 */
export async function reprintArchivedZBon(dateIso) {
  const archive = loadZBonArchive();
  const entry = archive[dateIso];
  if (!entry) {
    showToast('Z-Bon-Snapshot nicht gefunden.', 'error');
    return;
  }
  const snapshotHtml = await _archivedZBonHtml(dateIso, entry);
  if (!snapshotHtml) {
    showToast('Z-Bon-Snapshot nicht gefunden.', 'error');
    return;
  }
  try {
    // Wir extrahieren den Body aus dem Standalone-HTML — printReceiptHtml
    // wrappt selbst in <body class="printing-receipt">.
    const m = snapshotHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    const bodyHtml = m ? m[1] : snapshotHtml;
    printReceiptHtml(bodyHtml);

    // printedAt aktualisieren — Upsert in DB
    entry.printedAt = new Date().toISOString();
    if (window.App && window.App.storage && window.App.storage.zbonArchive) {
      await window.App.storage.zbonArchive.upsert(entry);
    }
    showToast('Druckdialog geöffnet.', 'success');
    closeViewArchivedZBon();
    if (typeof window.rerenderActiveTab === 'function') window.rerenderActiveTab();
  } catch (e) {
    console.error('[reprintArchivedZBon]', e);
    showToast('Druck fehlgeschlagen: ' + (e.message || e), 'error');
  }
}
